"use client"

import Link from "next/link"
import { ListingCard } from "@/components/marketing/content/listing-card"
import { ListingGrid } from "@/components/marketing/content/listing-grid"
import { ListingPagination } from "@/components/marketing/content/listing-pagination"
import { track } from "@/lib/analytics/track"
import { buildListingHref, LISTING_PAGE_SIZE } from "@/lib/marketing/pagination"
import type { BlogCategory } from "@/content/marketing/blog"

interface BlogPost {
  slug: string
  title: string
  description: string
  coverImage?: string
  category: BlogCategory
  readingTime: number
  publishedAt: string
}

interface CategoryDef {
  id: string
  label: string
}

const BASE_PATH = "/blog"

export function BlogListing({
  posts,
  categories,
  activeCategory,
  page,
  totalPages,
}: {
  posts: BlogPost[]
  categories: readonly CategoryDef[]
  activeCategory: string
  page: number
  totalPages: number
}) {
  const pills = [{ id: "all", label: "All" }, ...categories]

  return (
    <>
      {/* Category pills — real ?category= links; selecting one resets to page 1 */}
      <div className="flex flex-wrap gap-3">
        {pills.map((cat) => {
          const isActive = cat.id === activeCategory
          return (
            <Link
              key={cat.id}
              href={buildListingHref(BASE_PATH, { category: cat.id })}
              scroll={false}
              onClick={() => track("blog_category_filter", { category: cat.id })}
              className={`rounded-xl border px-5 py-3 text-sm font-medium transition-all ${
                isActive
                  ? "border-foreground bg-foreground text-background shadow-md"
                  : "border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted hover:text-foreground"
              }`}
            >
              {cat.label}
            </Link>
          )
        })}
      </div>

      {/* Post grid */}
      <div className="mt-8">
        {posts.length > 0 ? (
          <ListingGrid columns={2}>
            {posts.map((post, index) => (
              <ListingCard
                key={post.slug}
                href={`/blog/${post.slug}`}
                title={post.title}
                description={post.description}
                image={post.coverImage}
                category={categories.find((c) => c.id === post.category)?.label}
                meta={`${post.readingTime} min read · ${new Date(post.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })}`}
                onClick={() =>
                  track("blog_card_click", {
                    slug: post.slug,
                    position: (page - 1) * LISTING_PAGE_SIZE + index + 1,
                    category: post.category,
                  })
                }
              />
            ))}
          </ListingGrid>
        ) : (
          <div className="py-16 text-center">
            <p className="text-muted-foreground text-lg">
              No posts found.{" "}
              <Link
                href={BASE_PATH}
                className="text-foreground underline underline-offset-4 hover:no-underline"
              >
                Clear filter
              </Link>
            </p>
          </div>
        )}
      </div>

      <ListingPagination
        basePath={BASE_PATH}
        page={page}
        totalPages={totalPages}
        category={activeCategory}
      />
    </>
  )
}
